import { useDialog, type IDialog } from '@/features/interface/dialog'
import { ConcurrencyException, type Fetch } from '../types'

export type HttpResponseErrorFormaterResult = {
  statut: string
  content: string[]
}

export type HttpResponseErrorFormater = (response: Response) => Promise<HttpResponseErrorFormaterResult>

async function simpleFormater(response: Response): Promise<HttpResponseErrorFormaterResult> {
  const text = await response.text()

  const statut = response.statusText
    ? `<strong>Statut : ${response.status} ${response.statusText}</strong>`
    : `<strong>Statut : ${response.status} Erreur HTTP</strong>`

  try {
    const data = JSON.parse(text)
    return {
      statut,
      content: Object.keys(data)
        .filter((k) => !!data[k])
        .map((k) => `<strong>${k}</strong> : ${data[k]}`),
    }
  } catch {
    return {
      statut,
      content: text
        .split('\n')
        .map((str) => str.trim())
        .filter((str) => !!str),
    }
  }
}
const MSG_REESSAYER = '<strong>Voulez-vous réessayer ?</strong>' as const
export function configDialogFactory(errorFormater: HttpResponseErrorFormater = simpleFormater) {
  return function configDialog(nextFetch: Fetch): Fetch {
    const dialog: IDialog = useDialog()

    return async function (input: RequestInfo | URL, init?: RequestInit) {
      const dialogMode = init?.dialogMode ?? 'info'

      for (; ;) {
        try {
          const response = await nextFetch(input, init)
          if (response.ok || dialogMode === 'silent') return response
          else {
            // Ne traite pas les erreurs exclues
            if (init?.dialogExcludeErrors?.includes(response.status)) {
              return response
            }

            const responseClone = response.clone()
            const { statut, content } = await errorFormater(responseClone)

            const dialogMessage = [statut, ...content]

            const title = 'Le serveur a retourné une erreur'
            if (dialogMode === 'info') {
              await dialog.showDialog({
                type: 'Ok',
                title,
                message: dialogMessage,
                isHtml: true,
              })
              return response
            } else if (dialogMode === 'retry') {
              const message: string | string[] = [...dialogMessage, '', MSG_REESSAYER]
              const dialogResult = await dialog.showDialog({
                type: 'CancelRetry',
                title,
                message: message,
                isHtml: true,
              })
              if (dialogResult !== 'Retry') {
                return response
              }
            }
          }
        } catch (error) {
          const title = 'Le navigateur a retourné une erreur'
          if (dialogMode === 'silent' || error instanceof DOMException || error instanceof ConcurrencyException) {
            throw error
          }
          const erreurMsg = `<strong>${error}</strong>`
          if (dialogMode === 'info') {
            await dialog.showDialog({
              type: 'Ok',
              title,
              message: erreurMsg,
              isHtml: true,
            })
            throw error
          } else if (dialogMode === 'retry') {
            const dialogResult = await dialog.showDialog({
              type: 'CancelRetry',
              title,
              message: [erreurMsg, '', MSG_REESSAYER],
              isHtml: true,
            })
            if (dialogResult !== 'Retry') {
              throw error
            }
          }
        }
      }
    }
  }
}
